import { createElement, useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { SelectPanel } from './SelectPanel';

export interface MatSelectOptionItem {
  value: string;
  label: string;
}

interface MatSelectProps {
  /** Classes of the `<mat-select>` host (Angular `class=`). */
  className?: string;
  placeholder?: string;
  value: string | null | undefined;
  options: readonly MatSelectOptionItem[];
  onChange: (value: string) => void;
  /** Angular `panelClass`. */
  panelClass?: string;
}

/**
 * A bare `<mat-select>` (no form field around it, as the address and contact tables use it): the same host element,
 * trigger, value and arrow markup as Material, and its panel below it (`disableOptionCentering`).
 *
 * Behaviour of the Angular usage `<mat-select (focus)="x.open()" (valueChange)="x.close()">` on Material 17.3
 * (verified in the baseline build, see `PARITY_DECISIONS` D40):
 * - any `focus` of the host (Tab, mouse down, `focus()`) opens the panel; the trigger `click` toggles it, so a click
 *   that lands on the trigger itself (no time between mouse down and up) closes it again, while a real click ends on
 *   the panel's backdrop and leaves it open;
 * - choosing a different value closes the panel and does NOT give focus back (`valueChange` closes it first, and
 *   Material only restores focus while the panel is still open), so focus ends on the body;
 * - choosing the same value closes the panel and focuses the host; that focus opens it again when the mouse moved
 *   focus to the option, and does nothing when the keyboard did (the host never lost focus);
 * - Escape closes it and the host keeps focus without reopening; a backdrop click closes it and drops focus;
 * - with the panel closed the arrow keys, Home and End change the value directly; Enter/Space open the panel.
 */
export function MatSelect({ className = '', placeholder, value, options, onChange, panelClass }: MatSelectProps) {
  const valueId = useId();
  const ref = useRef<HTMLElement | null>(null);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [active, setActive] = useState(0);
  const isOpen = useRef(false);
  /** What to do with focus once the panel is gone (the panel's focus trap has let go by then). */
  const afterClose = useRef<'focus' | 'focus-silently' | null>(null);
  const silent = useRef(false);
  const selected = options.find((option) => option.value === value);
  const empty = !selected;
  const open = anchor !== null;

  const openPanel = () => {
    // MatSelect._canOpen: not open yet, and there is something to choose
    if (isOpen.current || options.length === 0) return;
    isOpen.current = true;
    setActive(
      Math.max(
        options.findIndex((option) => option.value === value),
        0,
      ),
    );
    setAnchor(ref.current);
  };
  const closePanel = (then: 'focus' | 'focus-silently' | null = null) => {
    isOpen.current = false;
    afterClose.current = then;
    setAnchor(null);
  };

  useEffect(() => {
    if (open || !afterClose.current) return;
    const mode = afterClose.current;
    afterClose.current = null;
    silent.current = mode === 'focus-silently';
    ref.current?.focus();
    silent.current = false;
  }, [open]);

  const move = (delta: number | 'first' | 'last') => {
    const current = options.findIndex((option) => option.value === value);
    const next = delta === 'first' ? 0 : delta === 'last' ? options.length - 1 : current + delta;
    if (options.length === 0 || next < 0 || next >= options.length || next === current) return;
    onChange(options[next].value);
  };

  const selectOption = (option: MatSelectOptionItem, source: 'mouse' | 'keyboard') => {
    if (option.value !== value) {
      closePanel();
      onChange(option.value);
    } else {
      // Material closes the panel and focuses the host: a mouse click moved focus off the host, so that
      // focus event opens the panel again; after Enter the host already had focus, so nothing happens
      closePanel(source === 'keyboard' ? 'focus-silently' : 'focus');
    }
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (open) {
      // Material keeps focus on the host while the panel is open and drives the active option from its key handler
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActive((index) => Math.min(index + 1, options.length - 1));
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActive((index) => Math.max(index - 1, 0));
      } else if (event.key === 'Home') {
        event.preventDefault();
        setActive(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        setActive(options.length - 1);
      } else if ((event.key === 'Enter' || event.key === ' ') && options[active]) {
        event.preventDefault();
        selectOption(options[active], 'keyboard');
      } else if (event.key === 'Escape') {
        event.preventDefault();
        closePanel('focus-silently');
      }
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openPanel();
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      event.preventDefault();
      move(1);
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      event.preventDefault();
      move(-1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      move('first');
    } else if (event.key === 'End') {
      event.preventDefault();
      move('last');
    }
  };

  return (
    <>
      {createElement(
        'mat-select',
        {
          ref,
          role: 'combobox',
          'aria-autocomplete': 'none',
          'aria-haspopup': 'listbox',
          'aria-labelledby': valueId,
          'aria-expanded': String(open),
          tabIndex: 0,
          className: `mat-mdc-select ${className}${empty ? ' mat-mdc-select-empty' : ''}`.trim(),
          onFocus: () => {
            if (!silent.current) openPanel();
          },
          onClick: () => (isOpen.current ? closePanel() : openPanel()),
          onKeyDown,
        },
        <div className="mat-mdc-select-trigger">
          <div className="mat-mdc-select-value" id={valueId}>
            {selected ? (
              <span className="mat-mdc-select-value-text">
                <span className="mat-mdc-select-min-line">{selected.label}</span>
              </span>
            ) : (
              placeholder && <span className="mat-mdc-select-placeholder mat-mdc-select-min-line">{placeholder}</span>
            )}
          </div>
          <div className="mat-mdc-select-arrow-wrapper">
            <div className="mat-mdc-select-arrow">
              <svg viewBox="0 0 24 24" width="24px" height="24px" focusable="false" aria-hidden="true">
                <path d="M7 10l5 5 5-5z" />
              </svg>
            </div>
          </div>
        </div>,
      )}
      <SelectPanel
        open={open}
        anchorEl={anchor}
        onClose={(reason) => closePanel(reason === 'escapeKeyDown' ? 'focus-silently' : null)}
        disableRestoreFocus
        keepFocus
        activeIndex={active}
        onActiveIndexChange={setActive}
        className={panelClass}
        labelledBy={valueId}
        exactWidth={false}
        options={options.map((option) => ({
          key: option.value,
          label: option.label,
          selected: option.value === value,
        }))}
        onSelect={(option, source) => selectOption({ value: option.key, label: option.label }, source)}
      />
    </>
  );
}
